import { Trash2 } from 'lucide-react';
import type { Song } from '@app/types';
import { KeyBadge } from '@app/components/ui';
import { lastPlayedLabel } from './songUtils';

interface SongItemProps {
  song: Song;
  onOpenDetail: (song: Song) => void;
  onDelete: (songId: string) => void;
  /** Kept for callers of the previous menu-based card; edit now lives on the song page. */
  onEdit?: (song: Song) => void;
  activeSongMenuId?: string | null;
  setActiveSongMenuId?: (id: string | null) => void;
}

/** Library card: key badge, tag, title, artist, then BPM · plays · last played. */
export const SongItem: React.FC<SongItemProps> = ({ song, onOpenDetail, onDelete }) => {
  const tag = song.tags?.[0];
  return (
    <div className="relative rounded-[20px] border border-line bg-card hover:border-line2 transition-colors">
      <button
        type="button"
        onClick={() => onOpenDetail(song)}
        className="flex flex-col gap-3.5 w-full text-left p-[18px] bg-transparent text-t1 rounded-[20px]"
      >
        <span className="flex justify-between items-start w-full">
          <KeyBadge k={song.defaultKey} tone="accent" size="lg" />
          {tag && <span className="text-[11.5px] font-semibold px-[9px] py-1 rounded-full bg-fill2 text-t3 mr-9">{tag}</span>}
        </span>
        <span>
          <span className="block text-[15.5px] font-semibold leading-[1.3]">{song.title}</span>
          <span className="block text-[13px] text-t4 mt-[3px]">{song.author}</span>
        </span>
        <span className="flex justify-between gap-2 w-full text-[12.5px] text-t4 border-t border-line pt-3">
          <span>
            <span className="font-mono text-t2">{song.defaultTempo ?? '–'}</span> bpm
          </span>
          {song.timesSung ? <span className="text-t2">Sung {song.timesSung}×</span> : null}
          <span>{lastPlayedLabel(song.lastPlayed)}</span>
        </span>
      </button>
      <button
        type="button"
        onClick={() => onDelete(song.id)}
        aria-label={`Delete ${song.title}`}
        className="absolute top-4 right-3.5 w-[30px] h-[30px] rounded-full border border-line2 bg-fill text-t4 hover:text-acc flex items-center justify-center"
      >
        <Trash2 size={13} strokeWidth={2} aria-hidden="true" />
      </button>
    </div>
  );
};
